"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { textToMusicSessionStorage } from "@/services/storage/text-to-music-session-storage";

export type TextToMusicSettings = {
  text: string;
  trackLength: number;
};

type TextToMusicContextState = {
  settings: TextToMusicSettings;
  handleTextChange: (text: string) => void;
  handleLengthChange: (trackLength: number) => void;
};

const TextToMusicContext = createContext<TextToMusicContextState | undefined>(
  undefined,
);

export const useTextToMusicContext = () => {
  if (!TextToMusicContext) {
    throw new Error("TextToMusicContext is undefined");
  }

  return useContext(TextToMusicContext)!;
};

const TextToMusicProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const initialSettings: TextToMusicSettings = {
    text: "",
    trackLength: 15,
  };

  const [settings, setSettings] =
    useState<TextToMusicSettings>(initialSettings);

  useEffect(() => {
    textToMusicSessionStorage.setDataFromStorage(setSettings);
  }, []);

  useEffect(() => {
    textToMusicSessionStorage.settingStorage.setSettings(settings.text, "text");
    textToMusicSessionStorage.settingStorage.setSettings(
      settings.trackLength,
      "trackLength",
    );
  }, [settings.text, settings.trackLength]);

  const handleTextChange = async (text: string) => {
    await textToMusicSessionStorage.settingStorage.setSettings(text, "text");
    setSettings((prev) => ({ ...prev, text }));
  };
  const handleLengthChange = async (trackLength: number) => {
    await textToMusicSessionStorage.settingStorage.setSettings(
      trackLength,
      "trackLength",
    );
    setSettings((prev) => ({ ...prev, trackLength }));
  };

  return (
    <TextToMusicContext.Provider
      value={{
        settings,
        handleTextChange,
        handleLengthChange,
      }}
    >
      {children}
    </TextToMusicContext.Provider>
  );
};

export default TextToMusicProvider;
